import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Box, IconButton } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import useAssistClip from '../../hooks/assistRequest/useAssistClip.hooks';
import { isAssistCardActive } from '../../redux/features/assistRequest/assistRequest.slice';
import {
  answerForOption,
  buildCardOptions,
  diffMarks,
} from '../../utils/assistRequest/hearAndConfirm/options';
import { IHearAndConfirmBody } from '../../utils/assistRequest/types';
import Colors from '../../utils/color';
import { ASSIST_SECTION_SX } from './AssistCardHeader';
import { IAssistBodyProps } from './assistBody.types';

const CHOICE_TEXT: Record<string, string> = {
  noise: 'Background noise',
  unclear: "Can't tell",
};

const MUTED_SX = { fontSize: 13, opacity: 0.7 } as const;

/** The words this option has that the other engine's text does not, marked. */
const OptionText = ({ text, other }: { text: string; other?: string }) => {
  if (other === undefined) return <>{text}</>;
  return (
    <>
      {diffMarks(text, other).map(({ word, differs }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && ' '}
          {differs ? (
            <Box
              component="mark"
              sx={{
                backgroundColor: Colors.gold,
                color: Colors.daintree,
                borderRadius: '3px',
                px: '2px',
              }}
            >
              {word}
            </Box>
          ) : (
            word
          )}
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * HITL-1783 (HITL 8.0.0): the hear-and-confirm body of the assigned card, in
 * two sections under the transcript.
 *
 * Audio (HITL-1786): pushed by live-transcriber, never waited for (contract
 * D11). Until the clip is in, the section says there is no audio yet and the
 * agent picks from the text; then Play shows, and the clip plays only when
 * the agent clicks it.
 *
 * Choices: one box per hearing (primary, secondary, then the primary's
 * alternatives) with the words they disagree on marked, then "Background
 * noise" and "Can't tell". Mouse only, no keys, no engine names. One click is
 * final: it sends the answer and the card ends. No typed answer for now: an
 * `edited` answer waits for the guardrail.
 */
/**
 * Clicks on the choices are ignored this long after a card shows (review
 * B2.2): a double click on the last card, or a click meant for the lobby, must
 * not vote on a card the agent has not read yet.
 */
export const ASSIST_CHOICE_ARM_MS = 300;

const HearAndConfirmBody = ({ card, onAnswer }: IAssistBodyProps) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(false);
    const timer = setTimeout(() => setArmed(true), ASSIST_CHOICE_ARM_MS);
    return () => clearTimeout(timer);
  }, [card.requestId]);
  const body = card.request.body as IHearAndConfirmBody;
  const options = useMemo(() => buildCardOptions(body), [body]);
  const live = isAssistCardActive(card);
  const { clip, play } = useAssistClip(card);

  const choiceSx = (secondary = false) => ({
    textAlign: secondary ? 'center' : 'left',
    font: 'inherit',
    fontSize: 14,
    lineHeight: 1.3,
    overflowWrap: 'anywhere',
    color: Colors.white,
    backgroundColor: secondary ? 'transparent' : Colors.elephant,
    border: `${secondary ? 1 : 2}px solid ${
      secondary ? Colors.dim : 'transparent'
    }`,
    borderRadius: 2,
    px: 1.5,
    py: secondary ? 0.75 : 1.25,
    cursor: live ? 'pointer' : 'default',
    '&:hover:not(:disabled)': { borderColor: Colors.cerulean },
    '&:focus-visible': { outline: `2px solid ${Colors.cerulean}` },
  });

  return (
    <Box data-testid="hear-and-confirm-body">
      <Box
        data-testid="assist-clip"
        data-clip-status={clip ? 'ready' : 'waiting'}
        sx={{
          ...ASSIST_SECTION_SX,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ...MUTED_SX,
        }}
      >
        {clip ? (
          <>
            <IconButton
              size="small"
              data-testid="assist-replay"
              aria-label="Play the guest's audio"
              disabled={!live}
              onClick={() => play()}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: Colors.cerulean,
                color: Colors.daintree,
                '&:hover': { backgroundColor: Colors.cerulean },
              }}
            >
              <PlayArrowRoundedIcon fontSize="small" />
            </IconButton>
            Play the guest&apos;s audio
          </>
        ) : (
          'No audio for this card yet - Pick from the text'
        )}
      </Box>

      <Box
        role="group"
        aria-label="Choices"
        sx={{
          ...ASSIST_SECTION_SX,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 1,
        }}
      >
        {options.map((option, index) => {
          const other =
            index === 0
              ? options[1]?.text
              : index === 1
              ? options[0]?.text
              : options[0]?.text;
          return (
            <Box
              key={`${option.choice}-${option.altIndex ?? index}`}
              component="button"
              type="button"
              data-testid={`assist-option-${index + 1}`}
              disabled={!live}
              onClick={() => armed && onAnswer(answerForOption(option))}
              sx={choiceSx()}
            >
              <OptionText text={option.text} other={other} />
            </Box>
          );
        })}
        {(
          [
            ['noise', 'assist-noise'],
            ['unclear', 'assist-unclear'],
          ] as const
        ).map(([choice, testId]) => (
          <Box
            key={choice}
            component="button"
            type="button"
            data-testid={testId}
            disabled={!live}
            onClick={() => armed && onAnswer({ answerChoice: choice })}
            sx={{
              ...choiceSx(true),
              // Noise and can't-tell always start their own row, apart from
              // the hearings, however many hearings there are.
              ...(choice === 'noise' ? { gridColumnStart: 1 } : {}),
            }}
          >
            {CHOICE_TEXT[choice]}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HearAndConfirmBody;
